import { useState, useEffect, useMemo } from 'react';
import * as _ from 'lodash';
import useInputValue from './useInputValue';

const DEFAULT_DEBOUNCE_TIMEOUT = 500;

/**
 * This hook expands on useInputValue by adding a debounced value.
 * This is useful for passing to the dependency array of a useCallback
 * which we are passing to useAsyncRequest, as it ensures that the value
 * is updated in real-time for the user, but so that we don't spam the
 * server with requests.
 *
 * @param {string} [initialValue] - Initial value on component mount.
 * @param {number} [debounceTimeout] - The debounce timeout in milliseconds.
 */
const useDebouncedInputValue = (initialValue: string = '', debounceTimeout = DEFAULT_DEBOUNCE_TIMEOUT) => {
  const [value, onChange] = useInputValue(initialValue);
  const [debouncedValue, setDebouncedValue] = useState<string>(initialValue);

  //  useMemo > useCallback here because useCallback expects an arrow function
  //  instead of the function returned by _.debounce.
  const onChangeDebouncedValue = useMemo(() =>
    _.debounce(setDebouncedValue, debounceTimeout), [debounceTimeout]);

  useEffect(() => {
    onChangeDebouncedValue(value);
  }, [value, onChangeDebouncedValue]);

  return {
    //  This is the real value of the input, updated in real-time.
    value,
    //  This is the change handler for the input.
    onChange,
    //  This represents the value after the debounce timeout.
    debouncedValue,
  };
};

export default useDebouncedInputValue;
